/**
 * What a plugin can reach (ADR 0025): every `nexus.*` call lands here and is
 * checked against the plugin's permissions.
 * - Reading sees what the AI may see: pages marked "hidden from AI" stay out,
 *   as plugin code may be written by an AI and may talk to the internet.
 * - Changes are the same checked actions as the AI assistant's; each one waits
 *   for the user to confirm it.
 * - Network calls go only to the domains in the manifest, over HTTPS.
 */
import {
  checkAction,
  toolContext,
  type ActionPreview,
  type ToolContext,
  type Undo,
} from '@nexus/ai';
import { serializeMarkdown } from '@nexus/markdown';
import type { Store } from '@nexus/store';
import { TEMPLATE_PROP, toIsoDate } from '@nexus/core';
import { loadPageJSON, loadTasks, summarizeProjects } from '@nexus/workspace';
import type { HostApi } from './sandbox';
import type { PluginManifest } from './manifest';

export const PLUGIN_DATA_PROP = 'nexus.plugin.data';
const MAX_DATA = 200_000;

export interface HttpRequest {
  method: 'GET' | 'POST';
  url: string;
  headers: Record<string, string>;
  body: string | null;
}

export interface HostDeps {
  store: Store;
  manifest: PluginManifest;
  /** The page that holds the plugin (its own storage lives there). */
  pluginPageId: string;
  /** Ask the user; true = do it. */
  confirm: (preview: ActionPreview) => Promise<boolean>;
  /** A change was made (for the activity log / undo). */
  onApplied?: (preview: ActionPreview, ran: { undo: Undo; pageId: string | null }) => Promise<void>;
  /** HTTPS request (through the proxy); absent = no network here. */
  fetch?: (req: HttpRequest) => Promise<{ status: number; text: string }>;
  toast: (text: string) => void;
  log: (line: string) => void;
  /** Builder preview: changes are only described, never made. */
  dryRun?: boolean;
}

export class PermissionError extends Error {}

const s = (v: unknown, max = 500) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const obj = (v: unknown) => (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
const visibleProps = (props: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(props).filter(([k]) => !k.startsWith('nexus.') && k !== 'ai'));

export function createHostApi(deps: HostDeps): HostApi {
  const { store, manifest } = deps;
  let ctx: ToolContext | null = null;
  // A fresh view of the workspace for each run is enough; reuse within a burst.
  let ctxAt = 0;
  const context = async () => {
    if (!ctx || Date.now() - ctxAt > 2_000) {
      ctx = await toolContext(store);
      ctxAt = Date.now();
    }
    return ctx;
  };
  const need = (what: 'read' | 'write') => {
    if (!manifest.permissions[what])
      throw new PermissionError(
        what === 'read'
          ? 'Deze plugin mag geen gegevens lezen (permissions.read).'
          : 'Deze plugin mag niets wijzigen (permissions.write).',
      );
  };
  const page = (c: ToolContext, id: string) => {
    const p = c.scope.visible.get(id);
    return p
      ? {
          id: p.id,
          title: p.title,
          icon: p.icon,
          parentId: p.parentId,
          props: visibleProps(p.props),
          created: new Date(p.createdAt).toISOString(),
          updated: new Date(p.updatedAt).toISOString(),
        }
      : null;
  };
  const pathOf = (c: ToolContext, id: string) => {
    const parts: string[] = [];
    for (let p = c.scope.visible.get(id), i = 0; p && i < 30; i++) {
      parts.unshift(p.title);
      p = p.parentId ? c.scope.visible.get(p.parentId) : undefined;
    }
    return parts.join('/');
  };

  // A preview keeps its storage in memory, starting from the installed data (if any).
  let previewData: Record<string, unknown> | null = null;
  const readData = async () =>
    previewData ??
    obj(
      deps.dryRun && !(await store.getPage(deps.pluginPageId))
        ? {}
        : (await store.getProps(deps.pluginPageId))[PLUGIN_DATA_PROP],
    );

  const write = async (action: string, input: Record<string, unknown>) => {
    need('write');
    const c = await context();
    const checked = await checkAction(c, action, input);
    if (deps.dryRun) return { applied: false, dryRun: true, preview: checked.preview };
    if (!(await deps.confirm(checked.preview))) return { applied: false };
    const ran = await checked.run();
    ctx = null;
    await deps.onApplied?.(checked.preview, ran);
    return { applied: true, pageId: ran.pageId };
  };

  const http = async (method: 'GET' | 'POST', rawUrl: unknown, body: unknown, options: unknown) => {
    let url: URL;
    try {
      url = new URL(s(rawUrl, 4000));
    } catch {
      throw new PermissionError('Ongeldige URL.');
    }
    const allowed = manifest.permissions.network.some(
      (d) => url.hostname === d || url.hostname.endsWith(`.${d}`),
    );
    if (url.protocol !== 'https:' || !allowed)
      throw new PermissionError(`Geen toegang tot ${url.hostname} (permissions.network).`);
    if (!deps.fetch) throw new PermissionError('Internet is hier niet beschikbaar.');
    const headers: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj(obj(options).headers)))
      if (typeof v === 'string' && /^[a-z0-9-]{1,60}$/i.test(k) && !/^(cookie|host)$/i.test(k))
        headers[k] = v.slice(0, 2000);
    const text =
      body === null || body === undefined
        ? null
        : typeof body === 'string'
          ? body
          : JSON.stringify(body);
    const res = await deps.fetch({ method, url: url.href, headers, body: text });
    let json: unknown = null;
    try {
      json = JSON.parse(res.text);
    } catch {
      // not JSON
    }
    return { status: res.status, text: res.text, json };
  };

  const calls: Record<string, (...args: unknown[]) => Promise<unknown> | unknown> = {
    today: () => toIsoDate(new Date()),
    toast: (text) => void deps.toast(s(text, 300)),
    log: (...parts) => void deps.log(parts.map(String).join(' ').slice(0, 2000)),

    'pages.search': async (query, limit) => {
      need('read');
      const c = await context();
      const n = typeof limit === 'number' ? Math.min(50, Math.max(1, limit)) : 20;
      const hits = (await store.search(s(query, 200), n * 3))
        .filter((h) => c.scope.visible.has(h.pageId))
        .slice(0, n);
      return hits.map((h) => ({
        ...page(c, h.pageId),
        path: pathOf(c, h.pageId),
        snippet: h.snippet,
      }));
    },
    'pages.get': async (id) => {
      need('read');
      return page(await context(), s(id, 64));
    },
    'pages.read': async (id) => {
      need('read');
      const c = await context();
      const pageId = s(id, 64);
      if (!c.scope.visible.has(pageId)) return null;
      return serializeMarkdown(await loadPageJSON(store, pageId), {
        titleOf: (x) => c.scope.visible.get(x)?.title,
      });
    },
    'pages.children': async (id) => {
      need('read');
      const c = await context();
      const parent = id === null ? null : s(id, 64);
      return [...c.scope.visible.values()]
        .filter((p) => p.parentId === parent && p.props[TEMPLATE_PROP] !== true)
        .map((p) => page(c, p.id));
    },
    'tasks.list': async (filter) => {
      need('read');
      const c = await context();
      const f = obj(filter);
      const status = s(f.status) || 'open';
      const project = s(f.project, 200).toLowerCase();
      const tag = s(f.tag, 100).replace(/^#/, '').toLowerCase();
      const under = s(f.under, 64);
      const limit = typeof f.limit === 'number' ? Math.min(2000, Math.max(1, f.limit)) : 500;
      const inside = (pageId: string) => {
        for (let p = c.scope.visible.get(pageId), i = 0; p && i < 30; i++) {
          if (p.id === under) return true;
          p = p.parentId ? c.scope.visible.get(p.parentId) : undefined;
        }
        return false;
      };
      return (await loadTasks(store))
        .filter((t) => c.scope.visible.has(t.pageId))
        .filter((t) =>
          status === 'all'
            ? true
            : status === 'done'
              ? t.status === 'done'
              : t.status !== 'done' && t.status !== 'cancelled',
        )
        .filter((t) => !s(f.dueBefore) || (!!t.due && t.due < s(f.dueBefore)))
        .filter((t) => !s(f.dueAfter) || (!!t.due && t.due > s(f.dueAfter)))
        .filter((t) => {
          if (!project) return true;
          const p = t.projectId ? c.scope.visible.get(t.projectId) : undefined;
          return t.projectId === project || p?.title.toLowerCase() === project;
        })
        .filter((t) => !tag || t.tags.some((x) => x.toLowerCase() === tag))
        .filter((t) => !under || inside(t.pageId))
        .slice(0, limit)
        .map((t) => ({
          id: t.id,
          title: t.title,
          status: t.status,
          priority: t.priority,
          due: t.due,
          dueTime: t.dueTime,
          doneAt: t.doneAt,
          tags: t.tags,
          projectId: t.projectId,
          project: t.projectId ? (c.scope.visible.get(t.projectId)?.title ?? null) : null,
          pageId: t.pageId,
          page: c.scope.visible.get(t.pageId)?.title ?? null,
        }));
    },
    'projects.list': async () => {
      need('read');
      const c = await context();
      const tasks = (await loadTasks(store)).filter((t) => c.scope.visible.has(t.pageId));
      return summarizeProjects([...c.scope.visible.values()], tasks).map((p) => ({
        id: p.page.id,
        title: p.page.title,
        status: p.status,
        deadline: p.deadline,
        open: p.open,
        done: p.done,
        overdue: p.overdue,
        progress: p.progress,
      }));
    },
    'databases.rows': async (id) => {
      need('read');
      const c = await context();
      const dbId = s(id, 64);
      return [...c.scope.visible.values()]
        .filter((p) => p.parentId === dbId && p.props[TEMPLATE_PROP] !== true)
        .map((p) => ({ id: p.id, title: p.title, props: visibleProps(p.props) }));
    },

    'pages.create': (input) => {
      const i = obj(input);
      return write('create_page', { title: i.title, parent_id: i.parentId, markdown: i.markdown });
    },
    'pages.append': (id, markdown) => write('append_to_page', { page_id: id, markdown }),
    'tasks.create': (input) => {
      const i = obj(input);
      return write('create_task', {
        title: i.title,
        due: i.due,
        time: i.time,
        priority: i.priority,
        page_id: i.pageId,
      });
    },
    'tasks.update': (id, changes) => write('update_task', { ...obj(changes), task_id: id }),

    'http.get': (url, options) => http('GET', url, null, options),
    'http.post': (url, body, options) => {
      // A preview may look things up, but not send anything.
      if (deps.dryRun) throw new PermissionError('In het voorbeeld verstuurt een plugin niets.');
      return http('POST', url, body, options);
    },

    'storage.get': async (key) => (await readData())[s(key, 100)] ?? null,
    'storage.set': async (key, value) => {
      const data = { ...(await readData()) };
      if (value === null) delete data[s(key, 100)];
      else data[s(key, 100)] = value;
      if (JSON.stringify(data).length > MAX_DATA)
        throw new PermissionError('De opslag van deze plugin is vol (200 kB).');
      if (deps.dryRun) previewData = data;
      else await store.setProp(deps.pluginPageId, PLUGIN_DATA_PROP, data);
      return null;
    },
  };

  return {
    call: async (method, args) => {
      const fn = calls[method];
      if (!fn) throw new Error(`Onbekende functie: nexus.${method}`);
      return fn(...args);
    },
  };
}
